"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Layers, Rocket, Users, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
type Language = "vi" | "en";
interface MetricData {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  valueVi: string;
  valueEn: string;
  labelVi: string;
  labelEn: string;
  detailVi?: string;
  detailEn?: string;
  accentClass: string;
  iconBgClass: string;
  iconColorClass: string;
}
const STATS_DATA: MetricData[] = [{
  id: "ratio",
  icon: Users,
  valueVi: "1:6",
  valueEn: "1:6",
  labelVi: "Tỷ lệ Giảng viên / Học viên",
  labelEn: "Mentor to Student Ratio",
  detailVi: "Tối đa 6 học viên mỗi lớp",
  detailEn: "Maximum 6 students per mentor",
  accentClass: "border-t-primary",
  iconBgClass: "bg-primary/10",
  iconColorClass: "text-primary"
}, {
  id: "age-range",
  icon: Rocket,
  valueVi: "4-16",
  valueEn: "4-16",
  labelVi: "Độ tuổi học viên",
  labelEn: "Student Age Range",
  detailVi: "tuổi (Mầm non đến THCS)",
  detailEn: "yrs (Preschool to Secondary)",
  accentClass: "border-t-accent",
  iconBgClass: "bg-accent/15",
  iconColorClass: "text-accent"
}, {
  id: "equipment",
  icon: Wrench,
  valueVi: "LEGO & AI",
  valueEn: "LEGO & AI",
  labelVi: "Học cụ chính",
  labelEn: "Core Equipment",
  detailVi: "LEGO Duplo & WhalesBot AI Module 1S",
  detailEn: "LEGO Duplo & WhalesBot AI Module 1S",
  accentClass: "border-t-secondary",
  iconBgClass: "bg-secondary/15",
  iconColorClass: "text-secondary"
}, {
  id: "pathway",
  icon: Layers,
  valueVi: "11 Cấp độ",
  valueEn: "11 Levels",
  labelVi: "Cấp độ lộ trình Robotics",
  labelEn: "Robotics Pathway Levels",
  detailVi: "3 giai đoạn: D1-D4, HP1-HP6, ENJOY AI",
  detailEn: "3 stages: D1-D4, HP1-HP6, ENJOY AI",
  accentClass: "border-t-primary",
  iconBgClass: "bg-primary/10",
  iconColorClass: "text-primary"
}, {
  id: "duration",
  icon: Clock,
  valueVi: "60 phút",
  valueEn: "60 min",
  labelVi: "Thời lượng học thử",
  labelEn: "Trial Session Length",
  detailVi: "Kèm 1:1 hướng dẫn trực tiếp",
  detailEn: "With direct instructor guidance",
  accentClass: "border-t-accent",
  iconBgClass: "bg-accent/15",
  iconColorClass: "text-accent"
}];
export default function AcademyStats() {
  const [lang, setLang] = useState<Language>("vi");
  useEffect(() => {
    const getStoredLang = (): Language => {
      if (typeof window === "undefined") return "vi";
      const stored = localStorage.getItem("roboto_lang");
      return stored === "en" ? "en" : "vi";
    };
    setLang(getStoredLang());
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang: Language;
      }>;
      if (customEvent.detail?.lang) {
        setLang(customEvent.detail.lang);
      } else {
        setLang(getStoredLang());
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        setLang(getStoredLang());
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isEn = lang === "en";
  return <section id="academystats" data-nav="light" className="relative bg-background text-foreground py-16 md:py-20 lg:py-24 border-b border-border overflow-hidden">
      {/* Background technical coordinate pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] select-none" aria-hidden="true" style={{
      backgroundImage: `radial-gradient(currentColor 1px, transparent 1px)`,
      backgroundSize: "24px 24px"
    }} />

      {/* Subtle top precision rail line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary opacity-80" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-3 uppercase font-mono tracking-wider text-xs px-2.5 py-1 border-border/80 bg-muted/60">
            {isEn ? "Verified Academy Metrics" : "Số liệu vận hành chuẩn"}
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            {isEn ? "ROBOTO by the Numbers" : "ROBOTO qua những con số"}
          </h2>

          <p className="mt-3.5 text-sm sm:text-base text-muted-foreground font-default leading-relaxed">
            {isEn ? "Verified academy operational indicators, rigorous mentor-to-student ratios, and standardized equipment standards." : "Bộ chỉ số vận hành đào tạo thực tế, kiểm soát nghiêm ngặt tỷ lệ lớp học và chuẩn mực học cụ chính khóa."}
          </p>
        </div>

        {/* 5-Column Responsive Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3.5 sm:gap-4 lg:gap-5">
          {STATS_DATA.map((item, index) => {
          const Icon = item.icon;
          const isLastOnMobile = index === STATS_DATA.length - 1;
          return <motion.div key={item.id} data-index={index} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.06,
            ease: "easeOut"
          }} className={`h-full ${isLastOnMobile ? "col-span-2 lg:col-span-1" : "col-span-1"}`}>
                <Card className={`h-full relative flex flex-col justify-between border border-border border-t-4 ${item.accentClass} bg-card text-card-foreground p-4 sm:p-5 shadow-xs hover:shadow-md transition-shadow duration-200 rounded-xl`}>
                  <div>
                    {/* Top row: Icon squircle & technical index */}
                    <div className="flex items-center justify-between gap-2 mb-3.5">
                      <div className={`size-9 sm:size-10 rounded-lg flex items-center justify-center shrink-0 ${item.iconBgClass} ${item.iconColorClass}`} aria-hidden="true">
                        <Icon className="size-5" />
                      </div>
                      <span className="font-mono text-[10px] sm:text-xs text-muted-foreground tracking-wider font-semibold">
                        {`0${index + 1}`}
                      </span>
                    </div>

                    {/* Stat Number */}
                    <div className="font-serif text-3xl sm:text-4xl xl:text-5xl font-bold tracking-tight text-foreground leading-none mb-2">
                      {isEn ? item.valueEn : item.valueVi}
                    </div>

                    {/* Label */}
                    <h3 className="font-mono text-xs sm:text-sm font-semibold text-foreground/90 leading-tight">
                      {isEn ? item.labelEn : item.labelVi}
                    </h3>
                  </div>

                  {/* Supporting text */}
                  {(item.detailVi || item.detailEn) && <div className="pt-3 mt-3 border-t border-border/60">
                      <p className="text-[11px] sm:text-xs text-muted-foreground font-default leading-snug line-clamp-2">
                        {isEn ? item.detailEn : item.detailVi}
                      </p>
                    </div>}
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}